import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import {
    Icon, Loader, InfoLabel, DateField 
} from '@jutro/components';
import { IntlContext, TranslatorContext } from '@jutro/locale';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { Currency as CurrencyField } from 'gw-components-platform-react';
import appConfig from 'app-config';
import metadata from './BillingTable.metadata.json5';
import styles from './BillingTable.module.scss';
import messages from './BillingTable.messages';
import { isCapabilityEnabled } from 'gw-portals-config-js';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import { useAuthentication } from 'gw-digital-auth-react';
import { getFontIcon } from '../../../module/nexus-portals-util-js/lobIconUtil';
import PropTypes from 'prop-types';
import { ButtonLink } from '@jutro/router';


const getCellPolicyNumber = (rowData) => {
    return (
        <span>
            {rowData.displayName}
        </span>
    );
};

const getCellBillingPlan = (rowData) => {
    return (
        <span>
            {rowData.paymentPlan}
        </span>
    );
};

const getCellLastPaymentDate = (rowData, rowIndex) => {

    let latestDate = new Date(rowData.lastPaymentDate)
    if (rowData.lastPaymentDate === undefined) {
        return (
            <span>
                No Payments Received
            </span>
        );
    }
    return (
        <DateField
            id={`latestDate_${rowIndex}`}
            value={rowData.lastPaymentDate}
            format="short"
            dataType={_.isString(rowData.lastPaymentDate) ? 'string' : 'object'}
            readOnly
            hideLabel
            layout="full-width"
            showOptional={false}
        />
    );
};

const getCellLastPayment = (rowData, rowIndex) => {
    if (rowData.lastPaymentRecieved === undefined || rowData.lastPaymentRecieved.amount == 0) {
        return (
            <span>
                -
            </span>
        );
    }
    return (
        <CurrencyField
            id={`paidAmount_${rowIndex}`}
            value={rowData.lastPaymentRecieved}
            dataType="object"
            readOnly
            hideLabel
        />
    );
};

const getCellBalanceDue = (rowData, rowIndex) => {
    if (rowData.balance === undefined || rowData.balance.amount == 0) {
        return (
            <span>
                -
            </span>
        );
    }
    return (
        <CurrencyField
            id={`dueAmount_${rowIndex}`}
            value={rowData.balance}
            dataType={_.isString(rowData.balance) ? 'string' : 'object'}
            readOnly
            hideLabel
        />
    );
}; 

const getCellBillingDetail = (rowData, rowIndex) => {
    return (
        <ButtonLink
            id={`viewBilling_${rowIndex}`}
            className={styles.link}
            to={{
                pathname: '/billing-summary/' + rowData.internalId
            }}
        >
            Details
        </ButtonLink>
    );
};

function BillingTable(props) {
    const {isAllBrokerPolicies, showDetails} = props;
    const translator = useContext(TranslatorContext);
    const [billingDetailsData, setBillingDetailsData] = useState([]);
    const [isLoading, setLoadingState] = useState(false);
    const { capabilitiesConfig } = appConfig;
    const { authHeader } = useAuthentication();

    const getCellProduct = (rowData) => {
        const lobCode = rowData.policyLOBCode;
        const icon = getFontIcon(lobCode);
        return <span className="iconFocus" tabindex="0"><Icon icon={icon} title={translator({ id: `typekey.LOBCode.${lobCode}`})} key={lobCode} /></span>;
    };   

    useEffect(() => {
        setLoadingState(true);
        try {
            if (isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'billing' })) {
                AccountBillingDetailsService.getUserBillingDetails_EY(authHeader).then(setBillingDetailsData, handleError);
            }
        } finally {
        setLoadingState(false);
        }
    }, []);

    const handleError = (error) => {
    }

    const generateBillingDetailsOverrides = useCallback(() => {
        return {
            productTypeColumn: { onCell: getCellProduct },
            policyNumberColumn: { onCell: getCellPolicyNumber },
            billingPlanColumn: { onCell: getCellBillingPlan },
            lastPaymentColumn: { onCell: getCellLastPayment },
            lastPaymentDateColumn: { onCell: getCellLastPaymentDate },
            balanceDueColumn: { onCell: getCellBalanceDue },
            billingViewColumn: {
                onCell: getCellBillingDetail,
                visible: showDetails
            }
        };
    }, [billingDetailsData]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrideProps = {
        billingTable: {
            data: billingDetailsData,
            visible: !_.isEmpty(billingDetailsData) && !isAllBrokerPolicies
        },
        billingEmpty: {
            visible: _.isEmpty(billingDetailsData) || isAllBrokerPolicies,
            content: isAllBrokerPolicies ? translator(messages.brokerBillingInfo) : translator(messages.billingEmpty)
        },     
        ...generateBillingDetailsOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
}

BillingTable.propTypes = {
};

export default BillingTable;
